import { useMemo, type ReactNode } from "react";

import TruthyCellPreview from "./TruthyCellPreview";

interface TextCellPreviewProps {
  isComputedDisplay: boolean;
  isError: boolean;
  isJson: boolean;
  richHtml: string | null;
  smartComputedTextClass: string;
  computedBooleanValue: boolean | null;
  textClass: string;
  tooltipText: string;
  globalSearchQuery: string;
  leadingIcon?: ReactNode | null;
}

const SEARCH_HIGHLIGHT_CLASS_NAME =
  "rounded-[3px] bg-amber-200/70 px-0.5 text-amber-950 ring-1 ring-amber-300/70 dark:bg-amber-300/20 dark:text-amber-100 dark:ring-amber-200/20";
const MAX_TITLE_LENGTH = 512;
const MAX_PREVIEW_TEXT_LENGTH = 1_000;

const getHighlightedTextNodes = (text: string, query: string): ReactNode[] | null => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return null;
  }

  const lowerText = text.toLocaleLowerCase();
  const lowerQuery = trimmedQuery.toLocaleLowerCase();
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let matchIndex = lowerText.indexOf(lowerQuery, cursor);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      nodes.push(text.slice(cursor, matchIndex));
    }

    const endIndex = matchIndex + trimmedQuery.length;
    nodes.push(
      <mark key={`${matchIndex}-${endIndex}`} className={SEARCH_HIGHLIGHT_CLASS_NAME}>
        {text.slice(matchIndex, endIndex)}
      </mark>,
    );

    cursor = endIndex;
    matchIndex = lowerText.indexOf(lowerQuery, cursor);
  }

  if (nodes.length === 0) {
    return null;
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return nodes;
};

export default function TextCellPreview({
  isComputedDisplay,
  isError,
  isJson,
  richHtml,
  smartComputedTextClass,
  computedBooleanValue,
  textClass,
  tooltipText,
  globalSearchQuery,
  leadingIcon = null,
}: TextCellPreviewProps) {
  const computedPrefix =
    isComputedDisplay && computedBooleanValue === null ? (
      <span className="flex-shrink-0 text-[11px] font-semibold leading-none text-slate-400 dark:text-slate-500">
        =
      </span>
    ) : null;

  const titleText = useMemo(() => tooltipText.slice(0, MAX_TITLE_LENGTH), [tooltipText]);
  const previewText = useMemo(() => tooltipText.slice(0, MAX_PREVIEW_TEXT_LENGTH), [tooltipText]);
  const highlightedContent = useMemo(
    () => getHighlightedTextNodes(previewText, globalSearchQuery),
    [globalSearchQuery, previewText],
  );

  let contentEl: ReactNode;

  if (highlightedContent) {
    contentEl = (
      <span
        className={`w-full min-w-0 flex-1 truncate text-sm ${isJson ? "font-mono" : ""} ${smartComputedTextClass} ${textClass || (isJson ? "text-gray-500 dark:text-gray-400" : "")}`}
        title={titleText}
      >
        {highlightedContent}
      </span>
    );
  } else if (richHtml) {
    contentEl = (
      <div
        className={`smart-table-cell-md w-full min-w-0 flex-1 overflow-hidden text-sm ${smartComputedTextClass} ${textClass}`}
        title={titleText}
        dangerouslySetInnerHTML={{ __html: richHtml }}
      />
    );
  } else if (computedBooleanValue === null) {
    contentEl = (
      <span
        className={`w-full min-w-0 flex-1 truncate text-sm ${isJson ? "font-mono" : ""} ${smartComputedTextClass} ${textClass || (isJson ? "text-gray-500 dark:text-gray-400" : "")}`}
        title={titleText}
      >
        {previewText}
      </span>
    );
  } else {
    contentEl = <TruthyCellPreview value={computedBooleanValue} label={titleText} />;
  }

  return (
    <div className="group/cell relative flex h-full w-full min-w-0 items-center gap-1.5 overflow-hidden">
      {isError && (
        <span className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-400" />
      )}

      {leadingIcon}

      {computedPrefix}

      {contentEl}
    </div>
  );
}
